import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { clearAllDrafts } from "../lib/operationDraft";

const AuthContext = createContext(null);

/**
 * docs-cadastro (contrato P1, auth.py `_pending_scope_fields`): nome do campo
 * de TOPO do token de escopo no 200 do register e no 403 do /login pendente.
 * Reexportado por components/ContractUpload; conferido contra o backend por
 * components/companyDocuments.contract.test.jsx.
 */
export const SCOPE_TOKEN_FIELD = "scope_token";

/**
 * 403 do cadastro PENDENTE com `scope_token` (+ `expires_in`) aditivo ⇒
 * `{ pending, scope_token, expires_in, message }`; qualquer outro erro ⇒ `null`.
 * Vale para o /login (sem 2FA) e, owner C1009746, para o /auth/2fa/verify do
 * pendente com 2FA ligado. Contrato P1 (auth.py): campos no TOPO do corpo, ao
 * lado de `detail` (string).
 */
function pendingScopeFromError(e) {
  const body = e?.response?.data;
  const scope = e?.response?.status === 403 ? body?.[SCOPE_TOKEN_FIELD] : null;
  if (!scope) return null;
  return {
    pending: true,
    scope_token: scope,
    expires_in: body?.expires_in,
    message: typeof body?.detail === "string" ? body.detail : "",
  };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.requires_2fa) {
        return { requires_2fa: true, temp_token: data.temp_token, email: data.email };
      }
      setUser(data.user);
      return { requires_2fa: false, user: data.user };
    } catch (e) {
      // docs-cadastro EAP-2 / PLAN PE-2: o 403 do cadastro PENDENTE continua
      // 403, com `scope_token` (+ `expires_in`) aditivo. Só esse ramo deixa de
      // lançar: devolve o token (memória, nunca storage) para a retomada do
      // envio dos obrigatórios. Qualquer outro erro (incl. 403 sem token,
      // ex. `rejected`/`inactive`) lança como antes.
      const pending = pendingScopeFromError(e);
      if (pending) return pending;
      throw new Error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    }
  };

  const verify2FA = async (temp_token, code) => {
    try {
      const { data } = await api.post("/auth/2fa/verify", { temp_token, code });
      setUser(data.user);
      return data.user;
    } catch (e) {
      // Owner C1009746: pendente com 2FA ligado ⇒ código válido devolve o MESMO
      // 403 + `scope_token` do /login pendente (sem sessão; `setUser` intacto).
      const pending = pendingScopeFromError(e);
      if (pending) return pending;
      throw new Error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      if (process.env.NODE_ENV === "development") console.error("Logout request failed:", e);
    }
    // FS-161: rascunhos de operação carregam PII (documento do sacado, NF,
    // valores). Quem sai não pode deixar isso para o próximo que logar na mesma
    // aba — e a limpeza roda MESMO se o POST de logout falhar (a sessão local
    // acaba de qualquer jeito, logo abaixo).
    clearAllDrafts();
    setUser(false);
  };

  const refreshMe = fetchMe;

  const contextValue = useMemo(() => ({
    user, loading, login, verify2FA, logout, refreshMe, setUser
  }), [user, loading, login, verify2FA, logout, refreshMe, setUser]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
